// Thin wrapper over the ChatGPT pixel snippet in index.html, mirroring
// MetaPixel.js. `oaiq` only exists on app.diffit.me for non-bot sessions — the
// snippet skips loading oaiq.min.js entirely otherwise — so this guard is what
// keeps every call site a no-op on localhost, PR previews, E2E runs and
// export-pipeline scrapes.

// Every measure call carries a data object whose `type` must match the event or
// the SDK drops it. That pairing is fixed by OpenAI's spec, so it lives here
// rather than at each call site: naming the event is enough to get the right
// shape, and a typo'd name is caught in dev instead of silently sending nothing.
// The full standard set, not just the three Diffit sends today — a partial copy
// of the spec would reject the next event someone adds.
const STANDARD_EVENT_SHAPES = {
  page_viewed: 'contents',
  contents_viewed: 'contents',
  items_added: 'contents',
  checkout_started: 'contents',
  order_created: 'contents',
  lead_created: 'customer_action',
  registration_completed: 'customer_action',
  appointment_scheduled: 'customer_action',
  subscription_created: 'plan_enrollment',
  trial_started: 'plan_enrollment',
};

const send = (eventName, data, options) => {
  if (typeof window !== 'undefined' && typeof window.oaiq === 'function') {
    // Passing an explicit `undefined` fourth argument is avoided rather than
    // assumed harmless — the SDK validates the options object when present.
    if (options) {
      window.oaiq('measure', eventName, data, options);
    } else {
      window.oaiq('measure', eventName, data);
    }
  }

  // Outside the oaiq guard on purpose: like fbq, `oaiq` is undefined in dev, so
  // an echo inside the guard would never fire and local call sites would be
  // unverifiable. Excluded from prod builds.
  if (import.meta.env.DEV) {
    console.log('[ChatGPT Pixel]\n\n', `measure ${options?.custom_event_name ?? eventName}`, {
      ...data,
    });
  }
};

// Neither export takes event parameters, and that is not an oversight. OpenAI's
// three data shapes accept only a fixed set of commerce fields (amount,
// currency, contents, plan_id) and explicitly no arbitrary metadata, so Meta's
// registration_method and is_new_signup have nowhere to go here. Those
// breakdowns stay in Mixpanel and GA, which is where they are reported from.

// One of OpenAI's standard events. Campaigns can optimize on these directly.
export function trackChatGPTEvent(name) {
  const type = STANDARD_EVENT_SHAPES[name];
  if (!type) {
    // A wrong name would otherwise reach the SDK with no shape and be dropped
    // server-side, which is invisible from the browser. Fail loudly in dev.
    if (import.meta.env.DEV) {
      console.error(`[ChatGPT Pixel] Unknown standard event "${name}" — not sent`);
    }
    return;
  }
  send(name, { type });
}

// A Diffit-defined event with no standard-event equivalent. OpenAI carries these
// under the reserved "custom" event name, with the real name in the options
// object; it must be 1-64 chars of letters/numbers/underscore/dash and must not
// collide with a standard event name.
export function trackChatGPTCustomEvent(name) {
  send('custom', { type: 'custom' }, { custom_event_name: name });
}
